import styled, { css } from 'styled-components';

import { BaseStyle } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import { WithHidden, WithIsHome, WithTeam } from '~/core/styleTypes';

import { getEntityBackgroundColor, getEntityTextAndBorderColor, getEntityMarkerBorder } from '../../../../utils';

export const GoalScorerContainer = styled.div<{ $isPptEmbed?: boolean }>`
    border-radius: 4px;
    border: ${({ $isPptEmbed, theme }) => ($isPptEmbed ? 'none' : `1px solid ${theme.Surface.border}`)};
    width: 100%;
`;

export const PlayerAndInfoContainer = styled.div`
    ${BaseStyle.Mixins.gaps.extraSmall}
    display: flex;
    flex-direction: column;
    align-self: stretch;
    justify-content: space-between;
    padding: 8px 12px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.gaps.small}
        padding: 16px;
        flex-direction: row;
    }
`;

export const TopRow = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
`;

export const PlayerInfo = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    height: 100%;
    margin-top: -5px;
    max-width: 240px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        margin-top: 0;
        max-width: 315px;
        gap: ${BaseStyle.GAP_SIZES.extraSmall};
    }
`;

export const PlayerInfoContainer = styled.div`
    ${BaseStyle.Mixins.gaps.small}
    display: flex;
    align-items: center;

    .PlayerInfo {
        ${BaseStyle.Mixins.gaps.extraSmall}

        ${BaseStyle.Mixins.screenSizes.medium} {
            ${BaseStyle.Mixins.gaps.small}
        }
    }

    ${BaseStyle.Mixins.screenSizes.large} {
        min-width: 340px;
    }
`;

export const PlayerName = styled.a`
    ${BaseStyle.Mixins.fontStyles.desktop.subtitle2}
    ${BaseStyle.Mixins.gaps.semiMedium}
    color: ${({ theme }) => theme.Text.primary};
    display: flex;
    align-items: center;
`;

export const AssistInformation = styled.div`
    ${BaseStyle.Mixins.fontStyles.desktop.body3}
    color: ${({ theme }) => theme.Text.secondary};
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;

    .team-display-container {
        flex: initial;
    }
`;

export const Assists = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const InfoContainer = styled.div<WithHidden & { $isEnglish: boolean }>`
    ${BaseStyle.Mixins.fontStyles.desktop.body3}
    ${BaseStyle.Mixins.gaps.tiny}
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    flex-direction: column;
    width: 100%;
    justify-content: center;
    align-items: center;
    color: ${({ theme }) => theme.Text.secondary};
    background-color: ${({ theme }) => theme.Surface.secondary};
    border-radius: 4px;
    padding: 4px;
    min-height: ${({ $isEnglish }) => ($isEnglish ? '100%' : '50px')};

    ${BaseStyle.Mixins.screenSizes.medium} {
        min-height: 100%;
        width: 100%;
        max-width: 105px;
        padding: 8px 4px;
        min-width: 68px;
    }
`;

export const GoalInfoContainer = styled.div<{ $noHighlightClip?: boolean }>`
    ${BaseStyle.Mixins.gaps.small}
    display: inline-flex;
    align-items: center;
    justify-content: space-between;

    ${BaseStyle.Mixins.screenSizes.medium} {
        width: 100%;
        max-width: 398px;
        justify-content: right;

        ${InfoContainer}:last-of-type {
            margin-right: ${({ $noHighlightClip }) => ($noHighlightClip ? '52px' : 0)};
        }
    }
`;

export const InfoData = styled.div<{ $isEnglish: boolean }>`
    ${({ $isEnglish }) => BaseStyle.Mixins.fontStyles.ui[$isEnglish ? '14b' : '12b']}
    color: ${({ theme }) => theme.Text.primary};
    text-transform: capitalize;
    text-align: center;
`;

export const ShootoutResultContainer = styled.div<{ $isGoal: boolean }>`
    width: 44px;
    height: 44px;
    justify-content: center;
    align-items: center;

    svg {
        fill: ${({ theme, $isGoal }) => ($isGoal ? theme.Utility.success : theme.Text.disabled)};
        height: 44px;
        width: 44px;
    }
`;

export const GoalSimulationExpander = styled.div`
    ${BaseStyle.Mixins.fontStyles.ui['14b']}
    ${BaseStyle.Mixins.gaps.small}

    display: flex;
    width: 100%;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    height: 36px;

    ${({ theme }) => css`
        border-top: 1px solid ${theme.Surface.border};
        color: ${theme.Utility.cta};

        svg {
            fill: ${theme.Utility.cta};
            height: 24px;
            width: 24px;
        }
    `}
`;

export const EntityMarker = styled.div<WithTeam & WithIsHome>`
    ${BaseStyle.Mixins.fontStyles.ui['10b']};
    height: 20px;
    width: 20px;
    text-align: center;
    align-content: center;
    margin: 4px -4px 4px 4px;
    color: ${({ theme, $teamAbbrev, $isHome }) => getEntityTextAndBorderColor(theme, $isHome, $teamAbbrev)};
    background: ${({ theme, $teamAbbrev, $isHome }) =>
        $teamAbbrev ? getEntityBackgroundColor(theme, $isHome, $teamAbbrev) : theme.Text.primary};
    border: 1px solid ${({ theme, $teamAbbrev, $isHome }) => getEntityMarkerBorder(theme, $teamAbbrev, $isHome)};
    outline: 1px solid ${({ theme, $teamAbbrev, $isHome }) => getEntityMarkerBorder(theme, $teamAbbrev, $isHome)};
    outline-offset: 2px;
    border-radius: 50%;
`;

export const ShootoutTeamDisplay = styled(TeamDisplay)`
    margin-right: -10px;
`;
